import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, FloppyDisk, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { updateProductClass, fetchPartSpecs, addPartSpec } from "@/lib/api";

export default function ProductClassEditModal({ row, user, onClose, onSaved }) {
  const [form, setForm] = useState({
    spec_1: row?.spec_1 || "",
    spec_1_options: row?.spec_1_options || "",
    spec_2: row?.spec_2 || "",
    spec_2_options: row?.spec_2_options || "",
    spec_3: row?.spec_3 || "",
    spec_3_options: row?.spec_3_options || "",
    needs_serial: row?.needs_serial || "No",
    in_price_list: String(row?.in_price_list) === "1" ? "1" : "0",
    prefix: row?.prefix || "",
    suffix: row?.suffix || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [suggestions, setSuggestions] = useState({ spec_1_values: [], spec_3_values: [] });
  const [newSpec1, setNewSpec1] = useState("");
  const [newSpec3, setNewSpec3] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!row?.computed_key) return;
    fetchPartSpecs(user.email, row.computed_key)
      .then(setSuggestions).catch(() => {});
  }, [row?.computed_key, user?.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await updateProductClass(row.id, { ...form, requester_email: user.email });
      onSaved?.();
      onClose();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  // Append an option to a comma-separated list without duplicates.
  const appendOption = (key, value) => {
    const v = (value || "").trim();
    if (!v) return;
    setForm((f) => {
      const current = (f[key] || "").split(/\s*,\s*/).filter(Boolean);
      if (current.includes(v)) return f;
      return { ...f, [key]: [...current, v].join(" , ") };
    });
  };

  const addNewToPool = async (field, value) => {
    const v = (value || "").trim();
    if (!v) return;
    try {
      await addPartSpec({
        [field === "spec_1_options" ? "spec_1" : "spec_3"]: v,
        for_class: row.computed_key,
        requester_email: user.email,
      });
      appendOption(field, v);
      if (field === "spec_1_options") setNewSpec1("");
      else setNewSpec3("");
      // Refresh suggestions pool
      fetchPartSpecs(user.email, row.computed_key).then(setSuggestions).catch(() => {});
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not add to pool");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-6" data-testid="pc-edit-overlay">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-sm shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]">
        <header className="px-4 md:px-6 py-3 border-b border-slate-200 flex items-center gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm md:text-base font-bold font-['Chivo'] text-slate-900 truncate">Edit Product Class</h2>
            <p className="text-[11px] text-slate-500 font-mono truncate">{row?.computed_key || row?.product_class}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-5 overflow-y-auto">
          {err && <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-sm border border-red-200">{err}</div>}

          {/* Flags */}
          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center justify-between gap-3 px-3 py-2 border border-slate-200 rounded-sm">
              <div>
                <div className="text-xs font-semibold text-slate-900">In Price List</div>
                <div className="text-[10px] text-slate-500">Shown on default price lists.</div>
              </div>
              <input data-testid="pc-edit-inlist" type="checkbox"
                checked={form.in_price_list === "1"}
                onChange={(e) => setForm((f) => ({ ...f, in_price_list: e.target.checked ? "1" : "0" }))}
                className="h-5 w-5 accent-blue-700" />
            </label>
            <label className="flex items-center justify-between gap-3 px-3 py-2 border border-slate-200 rounded-sm">
              <div>
                <div className="text-xs font-semibold text-slate-900">Needs Serial #</div>
                <div className="text-[10px] text-slate-500">Part requires a serial follow-up.</div>
              </div>
              <select data-testid="pc-edit-serial" value={form.needs_serial} onChange={set("needs_serial")}
                className="h-8 text-xs border border-slate-200 rounded-sm px-2 bg-white">
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
          </fieldset>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Prefix" value={form.prefix} onChange={set("prefix")} testId="pc-edit-prefix" />
            <TextField label="Suffix" value={form.suffix} onChange={set("suffix")} testId="pc-edit-suffix" />
          </div>

          <SpecBlock label="Spec 1" heading={form.spec_1} setHeading={set("spec_1")} testIdPrefix="spec1"
            options={form.spec_1_options} setOptions={set("spec_1_options")}
            pool={suggestions.spec_1_values}
            onAppend={(v) => appendOption("spec_1_options", v)}
            newValue={newSpec1} setNewValue={setNewSpec1}
            onAddPool={() => addNewToPool("spec_1_options", newSpec1)} />

          <fieldset>
            <legend className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">Spec 2 (no pool)</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Heading" value={form.spec_2} onChange={set("spec_2")} testId="pc-edit-spec2" />
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Allowed values</label>
                <textarea data-testid="pc-edit-spec2-opts" value={form.spec_2_options} onChange={set("spec_2_options")} rows={2}
                  className="w-full text-xs border border-slate-300 rounded-sm px-2 py-1 font-mono resize-none" />
              </div>
            </div>
          </fieldset>

          <SpecBlock label="Spec 3" heading={form.spec_3} setHeading={set("spec_3")} testIdPrefix="spec3"
            options={form.spec_3_options} setOptions={set("spec_3_options")}
            pool={suggestions.spec_3_values}
            onAppend={(v) => appendOption("spec_3_options", v)}
            newValue={newSpec3} setNewValue={setNewSpec3}
            onAddPool={() => addNewToPool("spec_3_options", newSpec3)} />

          <div className="flex gap-2 pt-3 border-t border-slate-200 sticky bottom-0 bg-white">
            <Button type="submit" disabled={saving} data-testid="pc-edit-save"
              className="flex-1 rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 text-sm font-semibold">
              <FloppyDisk size={14} weight="bold" className="mr-1" />
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}
              className="rounded-sm border-slate-200 hover:bg-slate-50 h-9 text-sm">Cancel</Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function TextField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <Input data-testid={testId} value={value || ""} onChange={onChange} className="h-9 text-sm rounded-sm border-slate-300" />
    </div>
  );
}

function SpecBlock({ label, heading, setHeading, options, setOptions, pool, onAppend, newValue, setNewValue, onAddPool, testIdPrefix }) {
  const currentSet = new Set((options || "").split(/\s*,\s*/).filter(Boolean));
  const available = (pool || []).filter((v) => !currentSet.has(v));

  return (
    <fieldset>
      <legend className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{label}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextField label="Heading" value={heading} onChange={setHeading} testId={`pc-edit-${testIdPrefix}`} />
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Allowed values (comma-separated)</label>
          <textarea data-testid={`pc-edit-${testIdPrefix}-opts`} value={options || ""} onChange={setOptions} rows={2}
            className="w-full text-xs border border-slate-300 rounded-sm px-2 py-1 font-mono resize-none" />
        </div>
      </div>

      {/* Suggestions pool */}
      <div className="mt-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
          <Sparkle size={11} weight="fill" className="text-amber-500" /> Recommendations from Part_Specs
        </div>
        {available.length === 0 ? (
          <div className="text-[11px] text-slate-400 italic">No suggestions — all pool values already added.</div>
        ) : (
          <div className="flex flex-wrap gap-1" data-testid={`pc-edit-${testIdPrefix}-pool`}>
            {available.map((v) => (
              <button key={v} type="button" onClick={() => onAppend(v)}
                data-testid={`pc-edit-${testIdPrefix}-pool-${v}`}
                className="text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded-sm truncate max-w-[200px]" title={`Append "${v}"`}>
                + {v}
              </button>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2">
          <Input placeholder={`Add new ${label} value`} value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            data-testid={`pc-edit-${testIdPrefix}-new-input`}
            className="h-8 text-xs rounded-sm border-slate-300 flex-1" />
          <Button type="button" onClick={onAddPool} disabled={!newValue.trim()}
            data-testid={`pc-edit-${testIdPrefix}-new-add`}
            className="rounded-sm bg-slate-700 text-white hover:bg-slate-800 h-8 px-2 text-xs font-semibold">
            <Plus size={12} weight="bold" className="mr-0.5" /> Save to pool & append
          </Button>
        </div>
      </div>
    </fieldset>
  );
}
